RivalIQ.apps.Analyze.module('views', function (views, App, Backbone, Marionette, $, _) {

    'use strict';

    function getFreeDimension(primaryMetric, secondaryMetric) {
        const freeDimensionsPrimaryMetric = primaryMetric.getFreeDimensions();
        if (secondaryMetric) {
            const freeDimensionsSecondaryMetric = secondaryMetric.getFreeDimensions();
            const freeDimensions = _.difference(freeDimensionsPrimaryMetric, freeDimensionsSecondaryMetric);
            return _.head(freeDimensions);
        }
    }

    const BarChart = views.BarChart = views.Chart.extend({
        renderedDataLimit: 15, // NOTE the default rendered bar maximum
        insufficientDataMessage: 'Not enough history',
        notApplicableDataMessage: '',

        onBeforeRenderLoaded: function () {
            const me = this;

            // We show the graph in `onShow`, which means the old view might still be hanging around, resulting in a flash of the old view.
            // Clear it out to prevent that.
            me.$el.html('');
        },

        onShow: function () {
            const me = this;

            let formatX;
            let popoverFormatX;
            let formatY;

            const graph = me.graph = me.getGraph();
            const timeAnalysisMode = me.getTimeAnalysisMode();

            graph.setTarget(me.el);
            graph.setAxisTicks(4);
            graph.setMarkerSize(5);
            graph.setMargins(me.getMargins());
            graph.setIsContentLinkable(me.isContentLinkable());
            graph.hasSubmetrics = me.hasSubmetrics();
            graph.submetrics = me.getSubmetrics();
            graph.excludedSubMetrics = _.clone(me.getProp('exclusions')) || [];
            graph.freeDimension = getFreeDimension(me.primaryMetric, me.secondaryMetric);
            graph.forceAllowClickableLegends = me.getProp('showBenchmarkLine');
            graph.focusCompanyId = me.getProp('focusCompanyId');
            graph.renderBarPatterns = me.getProp('renderBarPatterns');

            let noDataHtmlMessage = `We don't have enough data to show you this graph.`;
            if (!me.dashboardModel.isExporting()) {
                noDataHtmlMessage += '\nPlease select a different date range.';
            }

            graph.noDataHtmlMessage = noDataHtmlMessage;

            graph.isExportingPDF = me.dashboardModel.isExportingPDF();

            // TODO the model/metric should always know the format
            if (timeAnalysisMode === RivalIQ.enums.timeAnalysisMode.DELTA_RELATIVE) {
                formatX = formatY = popoverFormatX = '+.2%';
            } else {
                formatX = me.getFormatX();
                popoverFormatX = me.getPopoverFormatX();
            }

            // if we found a format for each axis set it
            if (formatX) {

                graph.setFormatX(formatX);
            }
            if (formatY) {
                graph.setFormatY(formatY);
            }
            if (popoverFormatX) {
                graph.setPopoverFormatX(popoverFormatX);
            }

            graph.setWidth(me.getWidth());

            me.data = me.getData();
            graph.setData(me.data);

            graph.showBenchmarkLine = me.getProp('showBenchmarkLine');

            if (graph.showBenchmarkLine) {
                const period2NumCompanies = _.get(me.benchmarkMetric.getCalculationMetadata(), ['period2NumCompanies']);
                if (period2NumCompanies <= 1) {
                    graph.showBenchmarkLine = false;
                }
            }

            if (graph.showBenchmarkLine) {
                graph.benchmarkData = {};

                const selectedBenchmarkCompareAgainst = RivalIQ.apps.Overview.utils.getSelectedBenchmarkCompareAgainst(me.getProp('selectedBenchmarkCompareAgainst'));

                const rawValue = _.get(me.benchmarkMetric.getValue({
                    format: false
                }), [0, 0]);

                const calculationMetadata = me.benchmarkMetric.getMetadata().calculationMetadata;
                const incompleteDataDetails = RivalIQ.utils.incompleteData.getBenchmarkIncompleteDataDetails(calculationMetadata, me.benchmarkMetric, selectedBenchmarkCompareAgainst);
                let benchmarkName = selectedBenchmarkCompareAgainst.name;

                const noCompetitorsAvailable = (selectedBenchmarkCompareAgainst.benchmarkCompaniesFilter === RivalIQ.enums.benchmarkCompaniesFilters.exclude_focus_company.id &&
                    calculationMetadata.period2NumCompanies === 0);

                if (!_.isNull(rawValue)) {
                    benchmarkName += `: ${me.benchmarkMetric.getValue()}`;
                }

                const hasDataIssues = (incompleteDataDetails.dataNotAvailable && !noCompetitorsAvailable) || incompleteDataDetails.partialDataMessage;
                if (hasDataIssues) {
                    graph.benchmarkData.notAvailableTooltip = incompleteDataDetails.dataNotAvailableTooltip || incompleteDataDetails.partialDataTooltip;
                }

                graph.benchmarkData.rawValue = rawValue;
                graph.benchmarkData.name = benchmarkName;
                graph.benchmarkData.description = selectedBenchmarkCompareAgainst.specificMetricDescription;
            }

            const height = me.getHeight(me.data);

            if (height) {
                graph.setHeight(height);
            }

            const nonNullValues = _.reject(me.data, datum => {
                return _.isNull(datum.value);
            });


            const metric = _.get(me.dashboardModel.getProp('metrics'), [0]);
            if (metric) {
                const metricSource = RivalIQ.metric.property.source(metric);
                if (RivalIQ.enums.sourceDetails[metricSource].privateData) {
                    noDataHtmlMessage = `No data can be displayed as no company in this landscape has access to ${RivalIQ.enums.sourceDetails[metricSource].name}.`;
                }
            }

            const excludedChannels = (me.primaryMetric.get('exclusionDimension') === 'channel' ? me.primaryMetric.get('exclusions') : []) || [];
            if (_.isEmpty(nonNullValues) &&
                me.getProp('showMessageOnDataNotAvailable') &&
                excludedChannels.length === 0 &&
                me.primaryMetric.get('source') === RivalIQ.enums.sources.CROSS_CHANNEL &&
                timeAnalysisMode === RivalIQ.enums.timeAnalysisMode.DELTA_ABSOLUTE &&
                me.primaryMetric.getNoHistoricData()) {

                const focusCompanyId = me.getProp('focusCompanyId');
                const company = me.getCompanyById(focusCompanyId);

                const timePeriodDate = RivalIQ.state.view.get('timePeriod').comparePeriodEnd;
                const sources = RivalIQ.data.channels.forIdentity().pluck('source');
                const missingSourcesList = _.map(sources, missingSource => {
                    const record = RivalIQ.utils.companyDisplay.getSourcePrimaryRecord(missingSource, company.attributes);
                    const earliestPresenceData = _.get(record, ['dates', 'earliestPresenceData']);

                    if (record && timePeriodDate.isBefore(earliestPresenceData)) {
                        return {
                            fontIcon: RivalIQ.enums.sourceDetails[missingSource].fontIcon,
                            date: earliestPresenceData
                        };
                    }
                });

                noDataHtmlMessage = Marionette.TemplateCache.get('#audienceDeltaNotAvailableTemplate')({
                    missingSourcesList: _.compact(missingSourcesList)

                });

                me.$el.html(noDataHtmlMessage);

                me.data = [];

            } else if (_.isEmpty(nonNullValues) &&
                !me.data.length && metric &&
                RivalIQ.enums.sourceDetails[RivalIQ.metric.property.source(metric)].privateData) {
                const View = RivalIQ.apps.Insights.views.getNoPresenceView(RivalIQ.metric.property.source(metric));
                const view = new View({
                    dashboardModel: me.dashboardModel,
                    el: me.el
                });
                view.render();

            } else if (me.data.length) {
                graph.render();
            } else {
                graph.render();

                if (!me.dashboardModel.isExporting()) {
                    // Explicitly render the legend as the graph won't by itself in the no data case.
                    graph.renderLegend();
                }
            }

            return me;
        },

        getGraph: function () {
            const me = this;
            if (!me.barGraph) {
                me.barGraph = new RivalIQ.graph.BarGraph({
                    pruneLegend: me.dashboardModel.isExporting()
                });

                me.listenTo(me.barGraph, 'exclusion:added', me.onChangeExcluded);
                me.listenTo(me.barGraph, 'exclusion:removed', me.onChangeExcluded);

                me.listenTo(me.barGraph, 'inclusion', me.onBarGraphInclusion);
                me.listenTo(me.barGraph, 'analyze-content', _.bind(me.showContentModal, me));
            }
            return me.barGraph;
        },

        getSortValue: function (datum) {
            const me = this;
            return me.getGraph().getDatumValue(datum);
        },

        // Parse numbers, and sort by value.
        dataSorter: function (a, b) {
            const me = this;
            const aa = me.getSortValue(a);
            const bb = me.getSortValue(b);

            if (aa === null && bb !== null) {
                return 1;
            } else if (bb === null && aa !== null) {
                return -1;
            } else if (aa === null && bb === null) {
                return a.name.toLowerCase() > b.name.toLowerCase();
            } else {
                return bb - aa;
            }
        },
        reverseDataSorter: function (a, b) {
            const me = this;
            const aa = me.getSortValue(a);
            const bb = me.getSortValue(b);

            // not enough data goes to the bottom
            if (aa === null && bb !== null) {
                return 1;
            } else if (bb === null && aa !== null) {
                return -1;
            } else if (aa === null && bb === null) {
                return a.name.toLowerCase() < b.name.toLowerCase();
            } else {
                return aa - bb;
            }
        },

        onChangeExcluded: function () {
            const me = this;
            const graph = me.getGraph();

            const freeDimension = getFreeDimension(me.primaryMetric, me.secondaryMetric);
            // NOTE we set the props silently b/c the graph redraws itself and
            // we dont want to redraw the whole graph, but we can only do that
            // if we don't have a secondary metric (because we need to go back
            // to the server to get the correct data)
            let silent = (freeDimension !== 'channel' && !me.getProp('showBenchmarkLine'));
            if (me.secondaryMetric) {
                silent = false;
            }

            const props = {
                exclusions: _.clone(graph.excludedSubMetrics)
            };

            if (freeDimension) {
                // TODO while we do discern that exclusion dimension, we can
                // only calculate `channel` related values for the time being
                // (i.e., we can't easily calculate `tweetType` change via
                // exclusions)
                props.exclusionDimension = freeDimension;
            }

            me.setProps(props, {
                silent: silent
            });

            if (silent) {
                graph.setData(me.getData());
                graph.render();
                graph.onAttachLoaded();
            }

            RivalIQ.execute('tracking:push:auth', RivalIQ.tracking.metric.GraphLegendClicked, {
                channel: me.getProp('channel'),
                freeDimension: freeDimension,
                chartType: 'barChart'
            });
        },

        getElisionMessage: function (n, elision, isStatic) {
            let message;

            if (!isStatic) {
                const direction = (elision === views.BarChart.ELIDE_ABOVE) ? 'above' : 'below';
                message = `Show ${n} ${direction}...`;
            } else {
                message = `${n} companies omitted`;
            }

            return message;
        },
        // If isStatic is true elision messages are not clickable
        elisionStrategy: function (data, limit, inclusions, isStatic) {
            const me = this;

            // if the limit is equal to or exceeds the length of the data then no action is required
            if (limit >= data.length) {
                return data;
            }

            // find the focus company
            const focusCompany = _.find(data, {
                focus: true
            });

            // now we need to find the location of the focus company in the list (effective rank)
            const indexOfFocusCompany = _.indexOf(data, focusCompany);

            let leaderLimit = limit; // how many we should take from the top
            let offsetAbove = 0; // how many should we take above the focus company
            let offsetBelow = 0; // how many should we take below the focus company
            let indexOfCohort = indexOfFocusCompany; // where is the group we care about located?

            // determine if the focus company is already within the view limit
            // remember to factor in the fact that the index is zero based but the limit is not
            if ((indexOfFocusCompany + 1) >= limit) {
                // if this is the bottom performer that is a special case
                if (indexOfFocusCompany === data.length - 1) {
                    leaderLimit = limit - views.BarChart.ELIDE_BOTTOM_LEADER_LIMIT;
                    offsetAbove = views.BarChart.ELIDE_BOTTOM_OFFSET_ABOVE;
                    offsetBelow = data.length - 1;
                } else {
                    // this applies to all other cases (middle, near the bottom, near the top)
                    leaderLimit = limit - views.BarChart.ELIDE_MIDDLE_LEADER_LIMIT;
                    offsetAbove = views.BarChart.ELIDE_MIDDLE_OFFSET_ABOVE;
                    offsetBelow = views.BarChart.ELIDE_MIDDLE_OFFSET_BELOW;
                }
            } else {
                // you are a leader, baby
                // we know we have at least one elision so, we need to make room (rebate)
                indexOfCohort = (--leaderLimit);
            }

            let cohort = data.slice(0, leaderLimit);

            // save us some time by determining if data has been included
            const hasInclusionAbove = Boolean(inclusions & views.BarChart.ELIDE_ABOVE);
            const hasInclusionsBelow = Boolean(inclusions & views.BarChart.ELIDE_BELOW);

            // we need to check if we don't have an elision below because we get a leader rebate
            if (indexOfCohort + offsetBelow >= data.length) {
                cohort = cohort.concat(data.slice(leaderLimit, leaderLimit += 2));
            }

            // do we need to do some magic above our cohort?
            if (offsetAbove > 0) {
                // check to see if we need to display this group
                if (hasInclusionAbove) {
                    cohort = cohort.concat(data.slice(leaderLimit, indexOfCohort - offsetAbove));
                } else {
                    // otherwise display the elision
                    cohort.push({
                        id: views.BarChart.ELIDE_ABOVE,
                        name: me.getElisionMessage(indexOfCohort - (leaderLimit + offsetAbove),
                            views.BarChart.ELIDE_ABOVE, isStatic),
                        disabled: isStatic,
                        value: void(0)
                    });
                }

                // as above, so below
                cohort = cohort.concat(data.slice(indexOfCohort - offsetAbove, indexOfCohort + offsetBelow));
            }

            // do we need to do some magic below our cohort?
            // If there is only one item to elide, just include it instead
            if (hasInclusionsBelow || data.length - (indexOfCohort + offsetBelow) === 1) {
                cohort = cohort.concat(data.slice(indexOfCohort + offsetBelow));
            } else if (indexOfCohort + offsetBelow < data.length) {
                // if we didn't already include the worse performer then display the elision
                cohort.push({
                    id: views.BarChart.ELIDE_BELOW,
                    name: me.getElisionMessage(data.length - (indexOfCohort + offsetBelow),
                        views.BarChart.ELIDE_BELOW, isStatic),
                    value: void(0),
                    disabled: isStatic
                });
            }

            return cohort;
        },

        getMargins: function () {
            const me = this;
            let right = 90;

            if (me.hasSecondaryData()) {
                // a bar chart with secondary data is showing current and % change
                right += 50;
            }

            return {
                top: 10,
                right: right,
                bottom: 40,
                left: (window.innerWidth < 480 ? 120 : 220)
            };
        },

        getCompanyById: function (companyId) {
            return RivalIQ.state.landscape.get('companies').get(companyId);
        },

        hasChannel: function (companyId) {
            const me = this;
            const metric = me.primaryMetric;
            const channel = metric.get('channel');
            // TODO we don't want to look at `companies` long term; we should
            // replace this approach with something better (metadata, metric)
            const company = me.getCompanyById(companyId);
            return RivalIQ.utils.companyDisplay.hasChannel(channel, company.attributes);
        },

        getEmptyDataMessage: function (companyId, subMetrics) {
            const me = this;
            let incompleteDataType;

            const company = me.getCompanyById(companyId);
            const companyName = _.escape((company ? company.get('name') : ''));
            const source = me.primaryMetric.get('source');
            const channelName = RivalIQ.enums.sourceDetails[source].name;

            if (me.hasChannel(companyId)) {
                if (me.isNotApplicable(companyId)) {
                    return me.notApplicableDataMessage;
                }

                // NOTE hack-o-rama ... if we are looking at instagram or cross-channel data, and we don't have
                // enough data, and the instagram handle is not a business profile, show a custom message
                const dataStatus = RivalIQ.apps.Shell.utils.getDataFreshnessState();
                let timePeriod = {};
                if (me.companyData) {
                    // on Social Posts
                    timePeriod = me.getProp('timePeriod');
                } else {
                    // on Custom Dashboards
                    timePeriod = me.primaryMetric.getDataTimePeriod();
                }

                const noHistoricData = me.primaryMetric.getNoHistoricData();
                const metricName = me.primaryMetric.getName();
                const timeAnalysisMode = me.primaryMetric.getTimeAnalysisMode();

                if (RivalIQ.enums.sourceDetails[source].supportsNotEnoughHistory) {
                    const excludedChannels = (me.primaryMetric.get('exclusionDimension') === 'channel' ? me.primaryMetric.get('exclusions') : []) || [];
                    const earliestDataDateByCompanyId = me.primaryMetric.getEarliestDataDate();
                    incompleteDataType = RivalIQ.utils.incompleteData.getIncompleteDataType(company, source, dataStatus.updateInFlight, timePeriod, earliestDataDateByCompanyId, noHistoricData, subMetrics, timeAnalysisMode, excludedChannels);
                    const errorText = RivalIQ.utils.incompleteData.getIncompleteDataText(incompleteDataType, company.attributes, timePeriod, companyName, metricName, source, earliestDataDateByCompanyId, null, noHistoricData, subMetrics, timeAnalysisMode, excludedChannels);
                    return errorText;
                }

                return {
                    message: me.insufficientDataMessage
                };
            }

            return {
                message: `No ${channelName} presence`,
                title: ['If you know the ', channelName, ' presence for ', `${companyName}, please add it in <a href="/companies">Companies</a>.`].join(''),
                unboldTitle: true
            };
        },
        getHeight: function (data) {
            const me = this;
            return Math.max(me.minHeight, data.length * me.itemHeight);
        },
        onBarGraphInclusion: function (inclusion) {
            const me = this;

            const inclusions = me.getProp('inclusions');
            me.setProps('inclusions', inclusions | inclusion, {
                silent: true
            });

            me.triggerMethod('show');
        },

        /**
         * A method to determine if a given company is in a "Not Applicable"
         * state for the current `primaryMetric`. At the moment this is only
         * possible when looking at a `primaryMetric` with a `timeAnalysisMode`
         * of DELTA_RELATIVE.
         */
        isNotApplicable: function (companyId) {
            const me = this;
            const deltaAbsoluteMetric = me.deltaAbsoluteMetric;

            if (!deltaAbsoluteMetric) {
                return false;
            }

            const companyValues = _.flatten(
                me.primaryMetric.clone({
                    companyId: companyId
                }).getValue({
                    format: false
                })
            );
            const companyValue = _.head(companyValues);

            if (companyValue !== null) {
                return false;
            }

            const companyDeltaAbsoluteValues = _.flatten(
                deltaAbsoluteMetric.clone({
                    companyId: companyId
                }).getValue({
                    format: false
                })
            );
            const companyDeltaAbsoluteValue = _.head(companyDeltaAbsoluteValues);

            return _.isNumber(companyDeltaAbsoluteValue);
        }
    });

    BarChart.ELIDE_ABOVE = 1;
    BarChart.ELIDE_BELOW = 2;

    BarChart.ELIDE_MIDDLE_LEADER_LIMIT = 7;
    BarChart.ELIDE_MIDDLE_OFFSET_ABOVE = 2;
    BarChart.ELIDE_MIDDLE_OFFSET_BELOW = 3;

    BarChart.ELIDE_BOTTOM_LEADER_LIMIT = 6;
    BarChart.ELIDE_BOTTOM_OFFSET_ABOVE = 2;
});
